/**
 * Core Card — Enterprise card container with multiple design variants,
 * header/footer composition, hover effects, and gradient accents.
 *
 * Variants:
 *   - default: Clean bordered card with subtle shadow
 *   - elevated: Lifted with stronger shadow, no border
 *   - outlined: Border-only, no shadow — minimal and flat
 *   - ghost: No border or shadow — blends with background
 *   - gradient: Gradient background with white text
 *   - glass: Glassmorphism effect — frosted glass
 *   - interactive: Elevated on hover with accent bar
 *
 * @example
 *   <Card variant="elevated"><CardHeader>Title</CardHeader><p>Body</p></Card>
 *   <Card variant="glass" hoverable><p>Frosted</p></Card>
 */
import React from 'react';
import { cn } from '@/lib/utils';

export type CardVariant = 'default' | 'elevated' | 'outlined' | 'ghost' | 'gradient' | 'glass' | 'interactive';

export interface CardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  hoverable?: boolean;
  /** Design variant */
  variant?: CardVariant;
  /** Padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Accent color — applies top border stripe (CSS color or var) */
  accent?: string;
}

export interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
  /** Description text below title */
  description?: string;
  /** Action elements (badges, buttons) on the right */
  actions?: React.ReactNode;
}

export interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

const paddingMap = { none: '', sm: 'p-3', md: 'p-5', lg: 'p-6' };

const variantClasses: Record<CardVariant, string> = {
  default: cn(
    'bg-white dark:bg-slate-900',
    'border border-slate-200 dark:border-slate-800',
    'shadow-sm',
  ),
  elevated: cn(
    'bg-white dark:bg-slate-900',
    'shadow-md dark:shadow-slate-950/40',
    'border border-transparent',
  ),
  outlined: cn(
    'bg-transparent',
    'border border-slate-200 dark:border-slate-700',
    'shadow-none',
  ),
  ghost: cn(
    'bg-slate-50/50 dark:bg-slate-800/30',
    'border border-transparent',
    'shadow-none',
  ),
  gradient: cn(
    'bg-gradient-to-br from-[var(--primary-color)] to-blue-600',
    'text-white',
    'border border-transparent',
    'shadow-lg shadow-blue-500/20 dark:shadow-blue-900/30',
  ),
  glass: cn(
    'bg-white/60 dark:bg-slate-900/60',
    'backdrop-blur-xl',
    'border border-white/20 dark:border-slate-700/40',
    'shadow-lg shadow-black/5 dark:shadow-black/20',
  ),
  interactive: cn(
    'bg-white dark:bg-slate-900',
    'border border-slate-200 dark:border-slate-800',
    'shadow-sm',
    'hover:shadow-lg hover:-translate-y-0.5',
    'transition-all duration-200 cursor-pointer',
    'group relative overflow-hidden',
  ),
};

export const Card: React.FC<CardProps> = ({
  children,
  className,
  onClick,
  hoverable = false,
  variant = 'default',
  padding = 'md',
  accent,
}) => (
  <div
    onClick={onClick}
    className={cn(
      paddingMap[padding],
      variantClasses[variant],
      hoverable && variant !== 'interactive' && 'hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group relative overflow-hidden',
      onClick && !hoverable && variant !== 'interactive' && 'cursor-pointer',
      className,
    )}
    style={{
      borderRadius: 'var(--container-border-radius, var(--border-radius, 8px))',
      ...(accent ? { borderTopColor: accent, borderTopWidth: '2px', borderTopStyle: 'solid' as const } : {}),
    }}
  >
    {/* Interactive accent bar */}
    {variant === 'interactive' && (
      <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-[var(--primary-color)] to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
    )}
    {children}
  </div>
);

export const CardHeader: React.FC<CardHeaderProps> = ({ children, className, description, actions }) => (
  <div className={cn('flex items-start justify-between mb-3', className)}>
    <div className="min-w-0">
      <div className="font-semibold text-sm text-slate-900 dark:text-slate-100">{children}</div>
      {description && (
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{description}</p>
      )}
    </div>
    {actions && <div className="flex items-center gap-1 ml-3 shrink-0">{actions}</div>}
  </div>
);

export const CardContent: React.FC<CardContentProps> = ({ children, className }) => (
  <div className={cn('text-sm text-slate-600 dark:text-slate-400', className)}>{children}</div>
);

export const CardFooter: React.FC<CardFooterProps> = ({ children, className }) => (
  <div className={cn('pt-3 border-t border-slate-100 dark:border-slate-800 mt-3', className)}>{children}</div>
);

export default Card;
